// src/services/firebase.js
import { initializeApp } from 'firebase/app';
import { 
  getAuth, 
  signInWithEmailAndPassword as firebaseSignIn,
  createUserWithEmailAndPassword,
  onAuthStateChanged,
  signOut as firebaseSignOut,
  updateProfile
} from 'firebase/auth';
import { 
  getFirestore, 
  collection, 
  addDoc, 
  getDocs, 
  doc, 
  updateDoc, 
  query, 
  where, 
  orderBy,
  getDoc,
  setDoc 
} from 'firebase/firestore';

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID
};

const app = initializeApp(firebaseConfig);
export const auth = getAuth(app);
export const db = getFirestore(app);

// Auth functions
export const signInWithEmailAndPassword = (email, password) => {
  return firebaseSignIn(auth, email, password);
};

export const signOut = () => {
  return firebaseSignOut(auth);
};

export { onAuthStateChanged, createUserWithEmailAndPassword, updateProfile, doc, setDoc };

// User functions
export const loadUserInfo = async (userId) => {
  try {
    const userDoc = await getDoc(doc(db, 'users', userId));
    if (userDoc.exists()) {
      // Update last login
      await updateDoc(doc(db, 'users', userId), {
        lastLogin: new Date().toISOString()
      });
      return userDoc.data();
    }
    return null;
  } catch (error) {
    console.error('Error loading user info:', error);
    return null;
  }
};

// Bet functions
export const addBet = async (betData, user, userInfo) => {
  try {
    const bet = {
      ...betData,
      userEmail: user.email,
      userId: user.uid,
      username: userInfo?.username || user.email.split('@')[0],
      tipster: betData.tipster || userInfo?.displayName || user.displayName || user.email,
      tipsterUid: betData.isTipsterBet ? user.uid : betData.originalTipsterUid || null,
      isTipster: userInfo?.isTipster || false,
      status: 'pending',
      date: new Date().toLocaleDateString(),
      timestamp: new Date().toISOString()
    };
    
    const docRef = await addDoc(collection(db, 'bets'), bet);
    
    // Update user stats
    if (userInfo) {
      await updateDoc(doc(db, 'users', user.uid), {
        'stats.totalBets': (userInfo.stats?.totalBets || 0) + 1
      });
    }
    
    return docRef.id;
  } catch (error) {
    console.error('Error adding bet:', error);
    throw error;
  }
};

export const loadBets = async (user, userInfo) => {
  try {
    const betsQuery = query(
      collection(db, 'bets'), 
      orderBy('timestamp', 'desc')
    );
    
    const snapshot = await getDocs(betsQuery);
    const bets = [];
    
    snapshot.forEach((doc) => {
      const betData = doc.data();
      // Show bet if:
      // 1. It's the user's own bet
      // 2. It's a public bet
      // 3. User is a tipster (can see all bets)
      if (betData.userId === user.uid || 
          betData.isPublic || 
          userInfo?.isTipster) {
        bets.push({ id: doc.id, ...betData });
      }
    });
    
    return bets;
  } catch (error) {
    console.error('Error loading bets:', error);
    return [];
  }
};

export const updateBet = async (betId, updates) => {
  try {
    const betRef = doc(db, 'bets', betId);
    await updateDoc(betRef, {
      ...updates,
      lastUpdated: new Date().toISOString()
    });
    
    // If status changed, update user stats
    if (updates.status && ['won', 'lost', 'push'].includes(updates.status)) {
      const betDoc = await getDoc(betRef);
      const betData = betDoc.data();
      
      if (betData.userId) {
        const userRef = doc(db, 'users', betData.userId);
        const userDoc = await getDoc(userRef);
        
        if (userDoc.exists()) {
          const userData = userDoc.data();
          const stats = userData.stats || {};
          
          // Update win/loss/push counts
          const updateStats = {
            [`stats.${updates.status === 'won' ? 'wins' : updates.status === 'lost' ? 'losses' : 'pushes'}`]: 
              (stats[updates.status === 'won' ? 'wins' : updates.status === 'lost' ? 'losses' : 'pushes'] || 0) + 1
          };
          
          // Calculate units profit/loss
          if (updates.status === 'won') {
            const profit = (parseFloat(betData.units || 1) * parseFloat(betData.odds)) - parseFloat(betData.units || 1);
            updateStats['stats.unitsProfit'] = (stats.unitsProfit || 0) + profit;
          } else if (updates.status === 'lost') {
            updateStats['stats.unitsProfit'] = (stats.unitsProfit || 0) - parseFloat(betData.units || 1);
          }
          
          await updateDoc(userRef, updateStats);
        }
      }
    }
  } catch (error) {
    console.error('Error updating bet:', error);
    throw error;
  }
};

export const loadTipsters = async () => {
  try {
    const tipstersQuery = query(
      collection(db, 'users'),
      where('isTipster', '==', true),
      where('isActive', '==', true)
    );
    
    const snapshot = await getDocs(tipstersQuery);
    const tipsters = [];
    
    snapshot.forEach((doc) => {
      const data = doc.data();
      const stats = data.stats || {};
      const winRate = (stats.wins && (stats.wins + stats.losses) > 0)
        ? ((stats.wins / (stats.wins + stats.losses)) * 100).toFixed(1)
        : '0.0';
      
      tipsters.push({
        id: doc.id,
        ...data,
        winRate,
        totalBets: stats.totalBets || 0,
        wins: stats.wins || 0,
        losses: stats.losses || 0
      });
    });
    
    return tipsters.sort((a, b) => parseFloat(b.winRate) - parseFloat(a.winRate));
  } catch (error) {
    console.error('Error loading tipsters:', error);
    return [];
  }
};